type TProgressData = {
  progress: number;
  calculate_percentage_of_recommended_time?: number | null;
  time_on_case: number;
  reset: number;
  kind: string;
};

// Helper functions grouped in progressBar object
const progressBar = {
  convertMillisecondsToMinutes: (ms: number): number => {
    if (!Number.isFinite(ms)) throw new Error("Invalid milliseconds");
    return Math.round(ms / 1000 / 60);
  },

  calculatePercentRecommendedTime: (
    timeOnCaseMs: number,
    recommendedTimeSeconds: number
  ): number => {
    if (
      !Number.isFinite(timeOnCaseMs) ||
      !Number.isFinite(recommendedTimeSeconds)
    ) {
      throw new Error("Invalid input for percent calculation");
    }
    if (recommendedTimeSeconds > 0) {
      return Math.min(
        (timeOnCaseMs / (recommendedTimeSeconds * 1000)) * 100,
        100
      );
    }
    return 0;
  },

  progressCircleTip: (
    color: string,
    type: string = "Case",
    isReset: boolean = false
  ): string => {
    switch (color) {
      case "green":
        return `${type} Completed`;
      case "low-time":
        return isReset
          ? `${type} Low Time With Reset (Completed in under 25% of recommended time)`
          : `${type} Low Time (Completed in under 25% of recommended time)`;
      default:
        return `${type} Progress Unknown`;
    }
  },

  setProgressBarAccessibility: (progress: number) => {
    const progressContainer = progressBar.getElement<HTMLDivElement>(".progress-container");
    if (progressContainer) {
      progressContainer.setAttribute("aria-valuenow", String(progress));
      let label = '';
      if (progress === 0 || isNaN(progress)) {
        label = 'Not Started';
      } else if (progress === 100) {
        label = '100% of the case is completed';
      } else {
        label = `${Math.round(progress)}% of the case is completed`;
      }
      progressContainer.setAttribute("aria-label", label);
    }
  },

  getElement: <T extends Element = Element>(selector: string): T | null => {
    if (!selector) throw new Error("Selector is required");
    return document.querySelector(selector) as T | null;
  },

  ensureProgressTextDiv: (): HTMLElement | null => {
    const progressBox = progressBar.getElement<HTMLDivElement>(".progress-box");
    if (!progressBox) return null;
    const parent = progressBox.parentElement;
    if (!parent) return null;
    let progressText = parent.querySelector<HTMLElement>(".progress-text");
    if (!progressText) {
      progressText = document.createElement("div");
      progressText.className = "progress-text";
      progressText.setAttribute("aria-live", "polite"); // Accessibility
      parent.insertBefore(progressText, progressBox);
    }
    return progressText;
  },

  setProgressBarWidth: (width: string) => {
    const bar = progressBar.getElement<HTMLDivElement>(".progress-bar-length");
    if (bar) bar.style.width = width;
  },

  setProgressText: (text: string) => {
    const progressText = progressBar.ensureProgressTextDiv();
    if (progressText) progressText.textContent = text;
  },

  showCompletionIcon: ({
    color,
    isReset,
  }: {
    color: string;
    isReset: boolean;
  }) => {
    const completeIconContainer = progressBar.getElement<HTMLDivElement>(
      "#progress-complete-icon"
    );
    if (!completeIconContainer) return;
    completeIconContainer.classList.remove("hide");
    const checkIcon = completeIconContainer.querySelector<HTMLElement>(
      '#completed-check-icon'
    );
    const clockIcon = completeIconContainer.querySelector<HTMLElement>(
      '#check-with-clock-icon'
    );
    const tooltip = completeIconContainer.querySelector<HTMLSpanElement>(
      ".tooltip-box.xsmall"
    );
    if (checkIcon) checkIcon.style.display = color === "green" ? "" : "none";
    if (clockIcon) clockIcon.style.display = color === "low-time" ? "" : "none";
    if (tooltip)
      tooltip.textContent = progressBar.progressCircleTip(
        color,
        "Case",
        isReset
      );
  },

  hideCompletionIcon: () => {
    const completeIconContainer = progressBar.getElement<HTMLDivElement>(
      "#progress-complete-icon"
    );
    if (completeIconContainer) completeIconContainer.classList.add("hide");
  },

  showMyTime: (timeOnCase: number) => {
    const myTime = progressBar.getElement<HTMLDivElement>(".my-time");
    const myTimeContent =
      progressBar.getElement<HTMLDivElement>(".my-time-content");
    if (!myTime || !myTimeContent) return;
    myTime.classList.remove("hide");
    myTimeContent.innerHTML = `<span class="whitespace-nowrap">My time:</span> <br/> ${progressBar.convertMillisecondsToMinutes(
      timeOnCase
    )} min`;
  },

  showResetButton: () => {
    const progressContainer = progressBar.getElement<HTMLDivElement>(
      ".progress-container"
    );
    if (progressContainer) {
      progressContainer.remove();
    }
    const resetQuestionWrapper = progressBar.getElement<HTMLDivElement>(
      "#resetQuestionButton"
    );
    if (resetQuestionWrapper) {
      resetQuestionWrapper.classList.remove("hide");
    }
    const resetCaseButton = document.querySelector<HTMLButtonElement>(
      "[data-reset-case-target='resetCaseButton']"
    );
    if (resetCaseButton) {
      resetCaseButton.classList.remove("hide");
    }
  },
};

const syncEndOfCaseText = (progress: number) => {
  const endOfCase = document.getElementById("end_of_case_text");
  if (!endOfCase) return;
  endOfCase.style.display = progress === 100 ? "" : "none";
};

export const updateProgress = (data: TProgressData) => {
  progressBar.setProgressBarAccessibility(data.progress);
  if (!data || !Number.isFinite(data.progress)) return;

  syncEndOfCaseText(data.progress);

  // Cache DOM elements once
  const progressBox = progressBar.getElement<HTMLDivElement>(".progress-box");
  const recommendedTimeElement = progressBar.getElement<HTMLSpanElement>(
    "#recommended-time-seconds"
  );
  const timeField =
    progressBar.getElement<HTMLInputElement>("#timeOnCaseField");
  const percentField = progressBar.getElement<HTMLInputElement>(
    "#percentRecommendedTimeField"
  );

  const progressPercent = `${data.progress}%`;
  const timeOnCase = Number.isFinite(data.time_on_case) ? data.time_on_case : 0;
  const recommendedTime = recommendedTimeElement
    ? parseInt(recommendedTimeElement.dataset.value || "0", 10)
    : 0;
  const percentRecommendedTime = progressBar.calculatePercentRecommendedTime(
    timeOnCase,
    recommendedTime
  );

  // Update hidden input fields
  if (timeField) timeField.value = String(timeOnCase);
  if (percentField) {
    percentField.value = String(
      Number.isFinite(data.calculate_percentage_of_recommended_time)
        ? data.calculate_percentage_of_recommended_time
        : percentRecommendedTime
    );
  }

  const isReset = Number(data.reset) > 0;
  if (progressPercent === "100%") {
    progressBar.setProgressText("");
    if (progressBox) progressBox.classList.add("hide");
    // If kind is 'illness_script', always show green check
    const showGreen = data.kind === "illness_script" ? true : percentRecommendedTime > 25;
    progressBar.showCompletionIcon({
      color: showGreen ? "green" : "low-time",
      isReset,
    });
    progressBar.showMyTime(timeOnCase);
    progressBar.showResetButton();
    return;
  }

  if (progressBox) progressBox.classList.remove("hide");
  if (data.progress === 0) {
    progressBar.setProgressText("Not Started");
    progressBar.setProgressBarWidth("0%");
    progressBar.hideCompletionIcon();
    return;
  }

  progressBar.setProgressText(`${progressPercent} Complete`);
  progressBar.setProgressBarWidth(progressPercent);
  progressBar.hideCompletionIcon();
};

// Make updateProgress globally available for offline sync
declare global {
  interface Window {
    updateProgress: typeof updateProgress;
  }
}

window.updateProgress = updateProgress;
